// ============================================
// Loading Skeleton Components
// ============================================

import React from 'react';

// 基礎骨架元件
interface SkeletonProps { className?: string; }
export const Skeleton = (props: SkeletonProps) => {
  const { className = '' } = props;
  return <div className={`animate-pulse bg-slate-200 rounded ${className}`}></div>;
};

// 表格骨架
const TableSkeletonComponent: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4,
}) => (
  <div className="w-full">
    {/* Header */}
    <div className="flex gap-4 p-4 border-b border-slate-100">
      {Array.from({ length: columns }).map((_, i) => (
        <div key={i}><Skeleton className="h-4 flex-1" /></div>
      ))}
    </div>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="flex gap-4 p-4 border-b border-slate-100">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <div key={colIndex}><Skeleton className="h-4 flex-1" /></div>
        ))}
      </div>
    ))}
  </div>
);
export const TableSkeleton = React.memo(TableSkeletonComponent);

// 卡片骨架
export const CardSkeleton: React.FC = () => (
  <div className="bg-white rounded-2xl shadow p-6 space-y-4">
    <Skeleton className="h-6 w-1/3" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-5/6" />
    <Skeleton className="h-4 w-4/6" />
  </div>
);

// 病歷卡片骨架
export const PatientCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-2xl shadow p-6">
    <div className="flex items-start gap-4">
      <Skeleton className="w-16 h-16 rounded-full" />
      <div className="flex-1 space-y-3">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-4 w-1/2" />
        <div className="flex gap-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-20" />
        </div>
      </div>
    </div>
  </div>
);

// 表單骨架
export const FormSkeleton: React.FC = () => (
  <div className="space-y-6">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-10 w-full" />
      </div>
    ))}
    <div className="flex gap-2">
      <Skeleton className="h-10 w-24" />
      <Skeleton className="h-10 w-24" />
    </div>
  </div>
);

// 詳情頁骨架
export const DetailSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex justify-between items-center">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-10 w-24" />
    </div>
    {/* Content sections */}
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="bg-white rounded-2xl shadow p-6 space-y-4">
        <Skeleton className="h-6 w-1/4" />
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, j) => (
            <div key={j} className="space-y-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-full" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// 時間軸骨架（Timeline-like list of events）
const TimelineSkeletonComponent: React.FC<{ items?: number }> = ({ items = 6 }) => (
  <ul className="space-y-3 text-sm">
    {Array.from({ length: items }).map((_, i) => (
      <li key={i} className="rounded-2xl border border-slate-100 bg-white p-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="mt-2 h-3 w-3/4" />
        <div className="mt-2">
          <Skeleton className="h-6 w-14 rounded-full" />
        </div>
      </li>
    ))}
  </ul>
);
export const TimelineSkeleton = React.memo(TimelineSkeletonComponent);

// Condensed snapshot skeleton (small metric list)
const SnapshotSkeletonComponent: React.FC<{ metrics?: number }> = ({ metrics = 7 }) => (
  <dl className="space-y-3" data-testid="snapshot-skeleton">
    {Array.from({ length: metrics }).map((_, i) => (
      <div key={i} className="flex justify-between items-center">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-4 w-16" />
      </div>
    ))}
  </dl>
);
export const SnapshotSkeleton = React.memo(SnapshotSkeletonComponent);

// Single metric row skeleton for progressive loading
export const MetricRowSkeleton: React.FC = () => (
  <div
    className="flex justify-between items-center py-0.5 transition-opacity duration-500 opacity-100 animate-fadein"
    data-testid="metric-row-skeleton"
    style={{ transition: 'opacity 0.5s' }}
  >
    <Skeleton className="h-3 w-20" />
    <Skeleton className="h-4 w-16" />
    <style>{`
      @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
      .animate-fadein { animation: fadein 0.5s; }
    `}</style>
  </div>
);

// ============================================
// Empty State Components
// ============================================

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
}) => (
  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
    {icon && <div className="mb-4 text-gray-400">{icon}</div>}
    <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
    {description && <p className="text-sm text-gray-500 mb-6 max-w-md">{description}</p>}
    {action && (
      <button
        onClick={action.onClick}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
      >
        {action.label}
      </button>
    )}
  </div>
);

// 常用空狀態變體
export const EmptyPatients: React.FC<{ onAdd: () => void }> = ({ onAdd }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
    }
    title="尚無病患資料"
    description="開始新增第一位病患，建立完整的醫療記錄。"
    action={{ label: '新增病患', onClick: onAdd }}
  />
);

export const EmptyAppointments: React.FC<{ onAdd: () => void }> = ({ onAdd }) => (
  <EmptyState
    icon={
      <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    }
    title="沒有預約記錄"
    description="新增預約，管理病患的看診時間。"
    action={{ label: '新增預約', onClick: onAdd }}
  />
);

export const EmptySearchResults: React.FC = () => (
  <EmptyState
    icon={
      <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
    }
    title="找不到相關結果"
    description="請嘗試調整搜尋條件或篩選器。"
  />
);

export const EmptyNotes: React.FC = () => (
  <EmptyState
    icon={
      <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    }
    title="尚無備註"
    description="新增備註以記錄重要資訊。"
  />
);

// ============================================
// Loading Overlay
// ============================================

interface LoadingOverlayProps {
  message?: string;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ message = '載入中...' }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <p className="text-gray-700">{message}</p>
    </div>
  </div>
);

// ============================================
// Progress Indicator
// ============================================

interface ProgressProps {
  current: number;
  total: number;
  label?: string;
}

export const Progress: React.FC<ProgressProps> = ({ current, total, label }) => {
  const percentage = Math.round((current / total) * 100);
  
  return (
    <div className="w-full">
      {label && (
        <div className="flex justify-between mb-2 text-sm">
          <span className="text-gray-700">{label}</span>
          <span className="text-gray-500">
            {current} / {total}
          </span>
        </div>
      )}
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};
